import { useQuery } from "@apollo/client";
import Close from "@mui/icons-material/Close";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import OpenInNew from "@mui/icons-material/OpenInNew";
import {
  Alert,
  Box,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { GridPaginationModel } from "@mui/x-data-grid-premium";
import DonutChart from "components/Dashboard/DonutChart";
import { CustomGridColDef, DataGrid } from "components/DataGrid";
import { gql } from "generated-graphql";
import {
  GetScoutConversationContextAnalysisForStaffQuery,
  GetScoutConversationContextAnalysisForStaffQueryVariables,
} from "generated-graphql/graphql";
import { useEffect, useMemo, useRef, useState } from "react";

import {
  cacheReadRatePct,
  countDistinctTurns,
  findLatestPrelude,
  prettySpendBucketType,
  rollupRoundtripsByTurn,
  windowStats,
  type TurnRollupRow,
} from "./scoutContextAnalysisStats";

export const GET_SCOUT_CONVERSATION_CONTEXT_ANALYSIS_FOR_STAFF = gql(`
  query GetScoutConversationContextAnalysisForStaff(
    $conversationId: ID!
    $page: Int
    $pageSize: Int
    $partType: String
    $documentAttachmentPage: Int
    $documentAttachmentPageSize: Int
  ) {
    getScoutConversationContextAnalysisForStaff(
      conversationId: $conversationId
      page: $page
      pageSize: $pageSize
      partType: $partType
      documentAttachmentPage: $documentAttachmentPage
      documentAttachmentPageSize: $documentAttachmentPageSize
    ) {
      conversationId
      count
      totalEstimatedTokens
      breakdown {
        type
        toolName
        count
        estimatedTokens
      }
      cache {
        cacheReadTokens
        cacheWriteTokens
        totalInputTokens
      }
      turns {
        messageId
        seq
        turnNumber
        roundtripIndex
        requestId
        traceId
        createdAt
        provider
        model
        inputTokens
        outputTokens
        thinkingTokens
        cacheReadTokens
        cacheWriteTokens
        systemPromptTokens
        toolDefinitionsTokens
        systemPromptText
        toolDefinitionsJson
        allowedSkillsJson
        conversationHistoryTokens
        estimatedCostUsd
        compactionApplied
        compactionLocus
        compactionJson
      }
      documentAttachments {
        count
        totalEstimatedCostUsd
        items {
          attachmentId
          privateDocumentId
          filename
          title
          ingestionMode
          extractionStatus
          pageCount
          createdAt
          previewModel
          previewInputTokens
          previewOutputTokens
          extractionModel
          extractionInputTokens
          extractionOutputTokens
          previewCostUsd
          extractionCostUsd
          estimatedCostUsd
        }
      }
      items {
        id
        conversationId
        messageId
        seq
        type
        streamPartId
        stepIndex
        agentState
        text
        toolName
        toolDisplayName
        toolCallId
        toolState
        toolInput
        toolOutput
        toolError
        toolTokenEstimate
        toolInputTokens
        toolDurationMs
        sourceId
        url
        title
        mediaType
        filename
        createdAt
        estimatedTokens
        message {
          id
          role
          seq
          requestId
          createdAt
        }
      }
    }
  }
`);

type PartRow = NonNullable<
  GetScoutConversationContextAnalysisForStaffQuery["getScoutConversationContextAnalysisForStaff"]
>["items"][number];
type TurnRow = NonNullable<
  GetScoutConversationContextAnalysisForStaffQuery["getScoutConversationContextAnalysisForStaff"]
>["turns"][number];
type DocumentAttachmentRow = NonNullable<
  GetScoutConversationContextAnalysisForStaffQuery["getScoutConversationContextAnalysisForStaff"]
>["documentAttachments"]["items"][number];

// Wider list than usual because the parts table is a deep-inspection tool —
// staff often want 50–100 rows visible at once without clicking through
// pages. The DataGrid's hidden "all" sentinel (-1) is what we ship as a
// null pageSize to the server when the user picks it.
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 250];
const SHOW_ALL_PAGE_SIZE = -1;
const SENTRY_ORG_SLUG = "encamp-98d563ed0";
const HISTORY_COLUMN_TOOLTIP =
  "Residual of this call's input after estimated system prompt and tool-definition overhead (chars/4).";

interface Props {
  conversationId: string;
}

function buildSentryTraceUrl(traceId: string): string {
  return `https://${SENTRY_ORG_SLUG}.sentry.io/explore/traces/trace/${traceId}`;
}

export function ScoutConversationContextAnalysis({ conversationId }: Props) {
  const theme = useTheme();
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 50,
  });
  const [documentPaginationModel, setDocumentPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });
  const [activePart, setActivePart] = useState<PartRow | null>(null);
  const [usageSummaryExpanded, setUsageSummaryExpanded] = useState(false);
  const [showSystemParts, setShowSystemParts] = useState(false);
  type SubTab = "parts" | "roundtrips" | "turns" | "documents" | "system" | "tools";
  const [subTab, setSubTab] = useState<SubTab>("roundtrips");

  const showAll = paginationModel.pageSize === SHOW_ALL_PAGE_SIZE;

  const variables: GetScoutConversationContextAnalysisForStaffQueryVariables = {
    conversationId,
    page: showAll ? null : paginationModel.page,
    pageSize: showAll ? null : paginationModel.pageSize,
    // Donut slices are spend-attribution buckets, not ScoutMessagePart types.
    partType: null,
    documentAttachmentPage: documentPaginationModel.page,
    documentAttachmentPageSize: documentPaginationModel.pageSize,
  };

  const { data, loading, error } = useQuery(GET_SCOUT_CONVERSATION_CONTEXT_ANALYSIS_FOR_STAFF, {
    variables,
    fetchPolicy: "cache-and-network",
  });

  const result = data?.getScoutConversationContextAnalysisForStaff;

  // Hold the last-known total part count steady across refetches. MUI's
  // DataGrid resets paginationModel.page to 0 the moment its rowCount prop
  // implies the current page is out of bounds — and during a server-paginated
  // refetch `data` is undefined, which would otherwise collapse rowCount to 0
  // and bounce us back to page 0 (then trigger a second refetch for that
  // page, then settle on whichever wins the race). Keeping rowCount sticky
  // breaks the loop. Reset only when the conversation changes.
  const lastKnownRowCountRef = useRef<number>(0);
  const lastKnownDocumentRowCountRef = useRef<number>(0);
  useEffect(() => {
    lastKnownRowCountRef.current = 0;
    lastKnownDocumentRowCountRef.current = 0;
    setDocumentPaginationModel({ page: 0, pageSize: 25 });
  }, [conversationId]);
  if (typeof result?.count === "number") {
    lastKnownRowCountRef.current = result.count;
  }
  const stableRowCount = lastKnownRowCountRef.current;
  if (typeof result?.documentAttachments?.count === "number") {
    lastKnownDocumentRowCountRef.current = result.documentAttachments.count;
  }
  const stableDocumentRowCount = lastKnownDocumentRowCountRef.current;

  // Roll up the per-(type, toolName) breakdown into per-type slices for
  // the donut. Depend on `result?.breakdown` directly rather than aliasing
  // with a fallback `?? []` — the alias would synthesise a new empty array
  // every render and trip react-hooks/exhaustive-deps.
  const typeSlices = useMemo(() => {
    const items = result?.breakdown ?? [];
    const byType = new Map<string, number>();
    for (const item of items) {
      byType.set(item.type, (byType.get(item.type) ?? 0) + item.estimatedTokens);
    }
    return Array.from(byType.entries())
      .filter(([, tokens]) => tokens > 0)
      .map(([type, tokens]) => ({ type, tokens }))
      .sort((a, b) => b.tokens - a.tokens);
  }, [result?.breakdown]);

  const totalTokens = result?.totalEstimatedTokens ?? 0;

  const seriesData = typeSlices.map((slice) => ({
    id: slice.type,
    value: slice.tokens,
    label: prettySpendBucketType(slice.type),
  }));

  const visibleParts = useMemo(() => {
    const items = result?.items ?? [];
    if (showSystemParts) {
      return items;
    }
    return items.filter((row) => row.message.role !== "system");
  }, [result?.items, showSystemParts]);

  // Contiguous "#" column instead of the raw ScoutMessagePart.seq —
  // ScoutMessage and ScoutMessagePart share a sequence counter, so showing
  // the raw seq exposes apparent gaps (1, 4, 5, 8…) that look like data
  // loss. Position is rebuilt from the current page offset so it stays
  // stable across pagination.
  const pageOffset = showAll ? 0 : paginationModel.page * paginationModel.pageSize;
  const indexByPartId = useMemo(() => {
    const map = new Map<string, number>();
    visibleParts.forEach((row, idx) => map.set(row.id, pageOffset + idx + 1));
    return map;
  }, [visibleParts, pageOffset]);

  const columns = useMemo<CustomGridColDef<PartRow>[]>(
    () => [
      {
        field: "position",
        headerName: "#",
        width: 60,
        type: "number",
        valueGetter: (_value, row) => indexByPartId.get(row.id) ?? row.seq,
      },
      {
        field: "type",
        headerName: "Type",
        width: 140,
        valueGetter: (_value, row) => prettyPartType(row.type),
      },
      {
        field: "role",
        headerName: "Role",
        width: 90,
        valueGetter: (_value, row) => row.message.role,
      },
      {
        field: "tool",
        headerName: "Tool",
        width: 180,
        valueGetter: (_value, row) => row.toolDisplayName ?? row.toolName ?? "—",
      },
      {
        field: "preview",
        headerName: "Preview",
        flex: 1,
        minWidth: 240,
        sortable: false,
        valueGetter: (_value, row) => previewForPart(row),
      },
      {
        field: "estimatedTokens",
        headerName: "Est. size",
        width: 100,
        type: "number",
        valueGetter: (_value, row) => row.estimatedTokens,
        valueFormatter: (value) => (Number(value) > 0 ? Number(value).toLocaleString() : "—"),
      },
    ],
    [indexByPartId]
  );

  const latestPrelude = useMemo(() => findLatestPrelude(result?.turns ?? []), [result?.turns]);

  const { latestInputTokens, peakInputTokens } = useMemo(
    () => windowStats(result?.turns ?? []),
    [result?.turns]
  );

  const documentAttachmentCostUsd = result?.documentAttachments?.totalEstimatedCostUsd ?? 0;

  const cacheReadPct = useMemo(
    () =>
      result?.cache
        ? cacheReadRatePct(result.cache.cacheReadTokens, result.cache.totalInputTokens)
        : null,
    [result?.cache]
  );

  const totalCostUsd = useMemo(() => {
    const turns = result?.turns ?? [];
    const roundtripCost = turns.reduce((sum, t) => sum + (t.estimatedCostUsd ?? 0), 0);
    return roundtripCost + documentAttachmentCostUsd;
  }, [result?.turns, documentAttachmentCostUsd]);

  const roundtripCount = result?.turns?.length ?? 0;

  return (
    // Flex column that exactly fills the parent: header + sub-tab strip +
    // active sub-tab content. The active tab uses flex:1 + overflow:hidden
    // and any data grid inside it virtualises its own scrollbar — no
    // double-scroll, no arbitrary height caps.
    <Stack spacing={1} sx={{ width: "100%", height: "100%", minHeight: 0, overflow: "hidden" }}>
      {error ? (
        <Alert severity="error">Failed to load context analysis: {error.message}</Alert>
      ) : null}

      <Paper variant="outlined" sx={{ flexShrink: 0 }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            px: 2,
            py: 1,
            cursor: "pointer",
            userSelect: "none",
            "&:hover": { backgroundColor: "action.hover" },
          }}
          onClick={() => setUsageSummaryExpanded((expanded) => !expanded)}
          role="button"
          aria-expanded={usageSummaryExpanded}
          aria-label={usageSummaryExpanded ? "Collapse provider usage" : "Expand provider usage"}
        >
          <Typography variant="subtitle2">Provider usage</Typography>
          {!usageSummaryExpanded ? (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              {[
                `${totalTokens.toLocaleString()} tokens processed`,
                totalCostUsd > 0 ? `$${totalCostUsd.toFixed(4)}` : null,
                cacheReadPct != null ? `${cacheReadPct}% cache read` : null,
                result ? `${result.count.toLocaleString()} parts` : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </Typography>
          ) : null}
          <Box sx={{ flex: 1 }} />
          {usageSummaryExpanded ? (
            <ExpandLess fontSize="small" color="action" />
          ) : (
            <ExpandMore fontSize="small" color="action" />
          )}
        </Stack>
        <Collapse in={usageSummaryExpanded}>
          <Box
            sx={{
              p: 2,
              pt: 0,
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              alignItems: "flex-start",
            }}
          >
            <Box
              sx={{
                // Fixed preferred width; maxWidth 100% so it can sit full-row when
                // the stats panel wraps below on a narrow canvas.
                flex: "0 1 320px",
                width: 320,
                maxWidth: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                Spend attribution (counted per call)
              </Typography>
              {seriesData.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ py: 6, textAlign: "center" }}
                >
                  No provider usage recorded yet.
                </Typography>
              ) : (
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <DonutChart
                    containerHeight="200px"
                    seriesData={seriesData}
                    seriesOverrides={{ innerRadius: 56, outerRadius: 90 }}
                    selectedIndex={null}
                    legend={{ position: "below", direction: "row" }}
                    colors={DONUT_COLORS}
                    legendSx={{ flexWrap: "wrap", rowGap: 0.5, maxWidth: "100%", px: 0 }}
                  />
                </Box>
              )}
              <Typography variant="caption" color="text.secondary">
                Totals sum every provider call. Resent history and the system prompt are counted
                each time. For window size, use Latest window or Per-roundtrip Input.
              </Typography>
            </Box>
            <Box
              sx={{
                // Basis ~2 card columns: when remaining row width is below this,
                // the panel wraps under the donut. auto-fill sizes to the panel
                // (not the viewport); minmax floor of ~25% caps at 4 columns.
                flex: "1 1 360px",
                minWidth: 0,
                display: "grid",
                gridTemplateColumns:
                  "repeat(auto-fill, minmax(max(160px, calc((100% - 36px) / 4)), 1fr))",
                gap: 1.5,
                alignItems: "stretch",
                alignContent: "start",
              }}
            >
              <ContextStatCard
                label="Tokens processed"
                value={totalTokens.toLocaleString()}
                hint={
                  roundtripCount > 0
                    ? `${roundtripCount.toLocaleString()} model call${
                        roundtripCount === 1 ? "" : "s"
                      }`
                    : undefined
                }
              />
              <ContextStatCard
                label="Latest window"
                value={latestInputTokens != null ? latestInputTokens.toLocaleString() : "—"}
                hint={
                  peakInputTokens != null ? `peak ${peakInputTokens.toLocaleString()}` : undefined
                }
              />
              <ContextStatCard
                label="Estimated cost"
                value={totalCostUsd > 0 ? `$${totalCostUsd.toFixed(4)}` : "—"}
              />
              <ContextStatCard
                label="Cache read rate"
                value={cacheReadPct != null ? `${cacheReadPct}%` : "—"}
                hint={
                  result?.cache && result.cache.totalInputTokens > 0
                    ? `${result.cache.cacheReadTokens.toLocaleString()} read · ${result.cache.cacheWriteTokens.toLocaleString()} write`
                    : undefined
                }
              />
              <ContextStatCard
                label="Input"
                value={(result?.cache?.totalInputTokens ?? 0).toLocaleString()}
              />
              <ContextStatCard
                label="Output"
                value={(
                  result?.turns?.reduce((s, t) => s + (t.outputTokens ?? 0), 0) ?? 0
                ).toLocaleString()}
              />
              <ContextStatCard
                label="Thinking"
                value={(
                  result?.turns?.reduce((s, t) => s + (t.thinkingTokens ?? 0), 0) ?? 0
                ).toLocaleString()}
              />
              <ContextStatCard label="Parts" value={(result?.count ?? 0).toLocaleString()} />
            </Box>
          </Box>
        </Collapse>
      </Paper>

      <Box sx={{ borderBottom: 1, borderColor: "divider", flexShrink: 0 }}>
        <Tabs
          value={subTab}
          onChange={(_, v: SubTab) => setSubTab(v)}
          sx={{ minHeight: 40, "& .MuiTab-root": { minHeight: 40 } }}
        >
          <Tab
            value="roundtrips"
            label={`Per-roundtrip${result?.turns ? ` (${result.turns.length})` : ""}`}
          />
          <Tab
            value="turns"
            label={`Per-turn rollup${
              result?.turns ? ` (${countDistinctTurns(result.turns)})` : ""
            }`}
          />
          <Tab value="parts" label={`Message parts${result ? ` (${result.count})` : ""}`} />
          <Tab
            value="documents"
            label={`Large doc extraction${
              result?.documentAttachments?.count ? ` (${result.documentAttachments.count})` : ""
            }`}
          />
          <Tab
            value="system"
            label={
              latestPrelude?.turnNumber != null
                ? `System prompt · turn ${latestPrelude.turnNumber}${
                    latestPrelude.systemPromptTokens
                      ? ` · ${latestPrelude.systemPromptTokens.toLocaleString()}`
                      : ""
                  }`
                : "System prompt"
            }
          />
          <Tab
            value="tools"
            label={
              latestPrelude?.toolDefinitionsTokens
                ? `Tool definitions · ${latestPrelude.toolDefinitionsTokens.toLocaleString()}`
                : "Tool definitions"
            }
          />
        </Tabs>
      </Box>

      <Paper
        variant="outlined"
        sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}
      >
        {subTab === "parts" ? (
          <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{
                px: 2,
                py: 1,
                borderBottom: 1,
                borderColor: "divider",
                flexShrink: 0,
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                Event log of unique parts. Not billed totals; system rows are per-turn snapshots.
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={showSystemParts}
                    onChange={(e) => setShowSystemParts(e.target.checked)}
                  />
                }
                label={<Typography variant="caption">Show system snapshots</Typography>}
                sx={{ mr: 0 }}
              />
            </Stack>
            <DataGrid<PartRow>
              rows={visibleParts}
              getRowId={(row) => row.id}
              columns={columns}
              loading={loading && !result}
              paginationMode="server"
              pagination
              rowCount={stableRowCount}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[
                ...PAGE_SIZE_OPTIONS,
                { value: SHOW_ALL_PAGE_SIZE, label: "Show all" },
              ]}
              onRowClick={(params) => setActivePart(params.row)}
              disableRowSelectionOnClick
              noRowsMessage="No parts persisted on this conversation yet."
              sx={{ flex: 1, border: "none" }}
            />
          </Box>
        ) : null}
        {subTab === "roundtrips" ? (
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <RoundtripUsageTable rows={result?.turns ?? []} />
          </Box>
        ) : null}
        {subTab === "turns" ? (
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <TurnRollupTable rows={rollupRoundtripsByTurn(result?.turns ?? [])} />
          </Box>
        ) : null}
        {subTab === "documents" ? (
          <DocumentAttachmentUsageTable
            rows={result?.documentAttachments?.items ?? []}
            loading={loading && !result}
            rowCount={stableDocumentRowCount}
            paginationModel={documentPaginationModel}
            onPaginationModelChange={setDocumentPaginationModel}
          />
        ) : null}
        {subTab === "system" ? (
          <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            {latestPrelude?.allowedSkillsJson ? (
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  borderBottom: 1,
                  borderColor: "divider",
                  backgroundColor: "action.hover",
                  flexShrink: 0,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Skill allow-list for turn {latestPrelude.turnNumber ?? "?"}:{" "}
                  <Box component="span" sx={{ fontFamily: "monospace" }}>
                    {latestPrelude.allowedSkillsJson}
                  </Box>
                </Typography>
              </Box>
            ) : null}
            <PreludePane
              text={latestPrelude?.systemPromptText ?? null}
              empty="No system prompt recorded yet — turns persisted before the prelude rollout don't have content. Send a new message to populate."
            />
          </Box>
        ) : null}
        {subTab === "tools" ? (
          <PreludePane
            text={
              latestPrelude?.toolDefinitionsJson
                ? prettyJson(latestPrelude.toolDefinitionsJson)
                : null
            }
            empty="No tool definitions recorded yet — see the System prompt tab note."
          />
        ) : null}
      </Paper>
      <Dialog
        open={Boolean(activePart)}
        onClose={() => setActivePart(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle
          sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
        >
          <span>
            Part #{activePart?.seq} — {activePart ? prettyPartType(activePart.type) : ""}
          </span>
          <IconButton size="small" onClick={() => setActivePart(null)}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {activePart ? (
            <Box
              component="pre"
              sx={{
                margin: 0,
                fontFamily: "monospace",
                fontSize: 12,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                backgroundColor: theme.palette.action.hover,
                p: 1.5,
                borderRadius: 1,
              }}
            >
              {JSON.stringify(activePart, null, 2)}
            </Box>
          ) : null}
        </DialogContent>
      </Dialog>
    </Stack>
  );
}

function ContextStatCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, height: "100%" }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2, mt: 0.25 }}>
        {value}
      </Typography>
      {hint ? (
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      ) : null}
    </Paper>
  );
}

function PreludePane({ text, empty }: { text: string | null; empty: string }) {
  if (!text) {
    return (
      <Box sx={{ p: 4, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography
          variant="body2"
          color="text.disabled"
          sx={{ maxWidth: 480, textAlign: "center" }}
        >
          {empty}
        </Typography>
      </Box>
    );
  }
  return (
    <Box
      component="pre"
      sx={{
        flex: 1,
        minHeight: 0,
        margin: 0,
        p: 2,
        overflow: "auto",
        fontFamily: "monospace",
        fontSize: 12,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
      }}
    >
      {text}
    </Box>
  );
}

function compactionKindFromJson(json: unknown): string | undefined {
  if (json === null || typeof json !== "object" || Array.isArray(json)) {
    return undefined;
  }
  if (!("kind" in json)) {
    return undefined;
  }
  const kind = json.kind;
  return typeof kind === "string" ? kind : undefined;
}

function prettyJson(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

function prettyPartType(type: string): string {
  switch (type) {
    case "text":
      return "Text";
    case "reasoning":
      return "Reasoning";
    case "tool":
      return "Tool";
    case "source_url":
      return "Source · URL";
    case "source_document":
      return "Source · Document";
    case "file":
      return "File";
    case "step_start":
      return "Step start";
    case "abort":
      return "Abort";
    default:
      return type;
  }
}

function previewForPart(part: PartRow): string {
  if (part.message.role === "system") {
    return "System prompt snapshot";
  }
  if (part.text) {
    return part.text;
  }
  if (part.toolDisplayName || part.toolName) {
    return `${part.toolDisplayName ?? part.toolName}${part.toolState ? ` · ${part.toolState}` : ""}`;
  }
  if (part.url) {
    return part.url;
  }
  if (part.filename) {
    return part.filename;
  }
  return "—";
}

function TurnRollupTable({ rows }: { rows: TurnRollupRow[] }) {
  const columns = useMemo<CustomGridColDef<TurnRollupRow>[]>(
    () => [
      { field: "turnNumber", headerName: "Turn", width: 60, type: "number" },
      { field: "roundtrips", headerName: "Calls", width: 70, type: "number" },
      {
        field: "model",
        headerName: "Model",
        width: 170,
        valueGetter: (_value, row) => row.model ?? "—",
      },
      {
        field: "traceId",
        headerName: "Trace",
        width: 70,
        sortable: false,
        filterable: false,
        renderCell: (params) =>
          params.row.traceId ? (
            <Tooltip title="Open Sentry trace">
              <IconButton
                component="a"
                href={buildSentryTraceUrl(params.row.traceId)}
                target="_blank"
                rel="noreferrer"
                size="small"
                aria-label="Open Sentry trace"
              >
                <OpenInNew fontSize="inherit" />
              </IconButton>
            </Tooltip>
          ) : (
            "—"
          ),
      },
      {
        field: "peakInputTokens",
        headerName: "Peak input",
        width: 100,
        type: "number",
        valueFormatter: (value) => formatTokens(Number(value)),
      },
      {
        field: "inputBilled",
        headerName: "Input billed",
        width: 110,
        type: "number",
        valueFormatter: (value) => formatTokens(Number(value)),
      },
      {
        field: "outputTokens",
        headerName: "Output",
        width: 90,
        type: "number",
        valueFormatter: (value) => formatTokens(Number(value)),
      },
      {
        field: "thinkingTokens",
        headerName: "Thinking",
        width: 100,
        type: "number",
        valueFormatter: (value) => formatTokens(Number(value)),
      },
      {
        field: "cacheReadTokens",
        headerName: "Cache read",
        width: 110,
        type: "number",
        valueFormatter: (value) => formatTokens(Number(value)),
      },
      {
        field: "cacheWriteTokens",
        headerName: "Cache write",
        width: 110,
        type: "number",
        valueFormatter: (value) => formatTokens(Number(value)),
      },
      {
        field: "estimatedCostUsd",
        headerName: "Cost",
        width: 100,
        type: "number",
        valueFormatter: (value) => (Number(value) > 0 ? `$${Number(value).toFixed(4)}` : "—"),
      },
    ],
    []
  );
  return (
    <DataGrid<TurnRollupRow>
      rows={rows}
      getRowId={(row) => row.turnNumber}
      columns={columns}
      hideFooter
      disableRowSelectionOnClick
      noRowsMessage="No turns yet."
      sx={{ border: "none", height: "100%" }}
    />
  );
}

function DocumentAttachmentUsageTable({
  rows,
  loading,
  rowCount,
  paginationModel,
  onPaginationModelChange,
}: {
  rows: DocumentAttachmentRow[];
  loading: boolean;
  rowCount: number;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
}) {
  const columns = useMemo<CustomGridColDef<DocumentAttachmentRow>[]>(
    () => [
      {
        field: "filename",
        headerName: "File",
        flex: 1,
        minWidth: 160,
        valueGetter: (_value, row) => row.filename ?? row.title ?? "—",
      },
      {
        field: "extractionStatus",
        headerName: "Status",
        width: 150,
        valueGetter: (_value, row) => prettyExtractionStatus(row.extractionStatus),
      },
      {
        field: "pageCount",
        headerName: "Pages",
        width: 70,
        type: "number",
        valueGetter: (_value, row) => row.pageCount,
        valueFormatter: (value) =>
          value != null && Number(value) > 0 ? Number(value).toLocaleString() : "—",
      },
      {
        field: "previewModel",
        headerName: "Preview model",
        width: 150,
        valueGetter: (_value, row) => row.previewModel ?? "—",
      },
      {
        field: "previewInputTokens",
        headerName: "Preview in",
        width: 100,
        type: "number",
        valueGetter: (_value, row) => row.previewInputTokens ?? 0,
        valueFormatter: (value) => formatTokens(Number(value)),
      },
      {
        field: "previewOutputTokens",
        headerName: "Preview out",
        width: 105,
        type: "number",
        valueGetter: (_value, row) => row.previewOutputTokens ?? 0,
        valueFormatter: (value) => formatTokens(Number(value)),
      },
      {
        field: "previewCostUsd",
        headerName: "Preview $",
        width: 95,
        type: "number",
        valueGetter: (_value, row) => row.previewCostUsd,
        valueFormatter: (value) => formatCostUsd(Number(value)),
      },
      {
        field: "extractionModel",
        headerName: "Extract model",
        width: 150,
        valueGetter: (_value, row) => row.extractionModel ?? "—",
      },
      {
        field: "extractionInputTokens",
        headerName: "Extract in",
        width: 100,
        type: "number",
        valueGetter: (_value, row) => row.extractionInputTokens ?? 0,
        valueFormatter: (value) => formatTokens(Number(value)),
      },
      {
        field: "extractionOutputTokens",
        headerName: "Extract out",
        width: 105,
        type: "number",
        valueGetter: (_value, row) => row.extractionOutputTokens ?? 0,
        valueFormatter: (value) => formatTokens(Number(value)),
      },
      {
        field: "extractionCostUsd",
        headerName: "Extract $",
        width: 95,
        type: "number",
        valueGetter: (_value, row) => row.extractionCostUsd,
        valueFormatter: (value) => formatCostUsd(Number(value)),
      },
      {
        field: "estimatedCostUsd",
        headerName: "Total",
        width: 100,
        type: "number",
        valueGetter: (_value, row) => row.estimatedCostUsd,
        valueFormatter: (value) => formatCostUsd(Number(value)),
      },
    ],
    []
  );

  return (
    <DataGrid<DocumentAttachmentRow>
      rows={rows}
      getRowId={(row) => row.attachmentId}
      columns={columns}
      loading={loading}
      paginationMode="server"
      pagination
      rowCount={rowCount}
      paginationModel={paginationModel}
      onPaginationModelChange={onPaginationModelChange}
      pageSizeOptions={PAGE_SIZE_OPTIONS}
      disableRowSelectionOnClick
      noRowsMessage="No large PDF extractions on this conversation yet."
      sx={{ border: "none", height: "100%" }}
    />
  );
}

function prettyExtractionStatus(status: string): string {
  switch (status) {
    case "NOT_ATTEMPTED":
      return "Not attempted";
    case "PREVIEW_RUNNING":
      return "Preview running";
    case "AWAITING_CONFIRMATION":
      return "Awaiting confirmation";
    case "FULL_ANALYSIS_DECLINED":
      return "Declined";
    case "PENDING":
      return "Pending";
    case "RUNNING":
      return "Running";
    case "SUCCEEDED":
      return "Succeeded";
    case "FAILED":
      return "Failed";
    default:
      return status;
  }
}

function formatCostUsd(cost: number): string {
  if (!Number.isFinite(cost) || cost <= 0) {
    return "—";
  }
  return `$${cost.toFixed(4)}`;
}

function RoundtripUsageTable({ rows: turns }: { rows: TurnRow[] }) {
  const latestId = turns.length > 0 ? turns[turns.length - 1]?.messageId : undefined;

  const columns = useMemo<CustomGridColDef<TurnRow>[]>(
    () => [
      {
        field: "seq",
        headerName: "Turn.RT",
        width: 80,
        valueGetter: (_value, row) =>
          row.turnNumber != null && row.roundtripIndex != null
            ? `${row.turnNumber}.${row.roundtripIndex}`
            : `${row.seq}`,
        renderCell: (params) => {
          const label =
            params.row.turnNumber != null && params.row.roundtripIndex != null
              ? `${params.row.turnNumber}.${params.row.roundtripIndex}`
              : `${params.row.seq}`;
          const isLatest = params.row.messageId === latestId;
          return (
            <Typography
              variant="body2"
              sx={{ fontWeight: isLatest ? 700 : 400, lineHeight: "inherit" }}
            >
              {label}
            </Typography>
          );
        },
      },
      {
        field: "model",
        headerName: "Model",
        width: 170,
        valueGetter: (_value, row) => row.model ?? "—",
      },
      {
        field: "traceId",
        headerName: "Trace",
        width: 70,
        sortable: false,
        filterable: false,
        renderCell: (params) =>
          params.row.traceId ? (
            <Tooltip title="Open Sentry trace">
              <IconButton
                component="a"
                href={buildSentryTraceUrl(params.row.traceId)}
                target="_blank"
                rel="noreferrer"
                size="small"
                aria-label="Open Sentry trace"
              >
                <OpenInNew fontSize="inherit" />
              </IconButton>
            </Tooltip>
          ) : (
            "—"
          ),
      },
      {
        field: "inputTokens",
        headerName: "Input",
        width: 90,
        type: "number",
        valueGetter: (_value, row) => row.inputTokens ?? 0,
        valueFormatter: (value) => formatTokens(Number(value)),
      },
      {
        field: "systemPromptTokens",
        headerName: "System prompt",
        width: 130,
        type: "number",
        valueGetter: (_value, row) => row.systemPromptTokens ?? 0,
        valueFormatter: (value) => formatTokens(Number(value)),
      },
      {
        field: "toolDefinitionsTokens",
        headerName: "Tool defs",
        width: 110,
        type: "number",
        valueGetter: (_value, row) => row.toolDefinitionsTokens ?? 0,
        valueFormatter: (value) => formatTokens(Number(value)),
      },
      {
        field: "conversationHistoryTokens",
        headerName: "History",
        width: 100,
        type: "number",
        description: HISTORY_COLUMN_TOOLTIP,
        valueGetter: (_value, row) => row.conversationHistoryTokens ?? 0,
        valueFormatter: (value) => formatTokens(Number(value)),
        renderHeader: () => (
          <Tooltip title={HISTORY_COLUMN_TOOLTIP}>
            <span>History</span>
          </Tooltip>
        ),
      },
      {
        field: "compactionLocus",
        headerName: "Compaction",
        width: 120,
        description:
          "Fence (summary) or tool_clear on the call that consumed the smaller window. tool_clear is roundtrip metadata only — not a hydrate fence.",
        valueGetter: (_value, row) => {
          if (!row.compactionApplied) {
            return null;
          }
          const kind = compactionKindFromJson(row.compactionJson);
          if (kind === "tool_clear") {
            return "tool_clear";
          }
          return row.compactionLocus ?? "applied";
        },
        renderCell: (params) => {
          const locus = params.value;
          if (!locus) {
            return "—";
          }
          const json = params.row.compactionJson;
          const tooltip =
            json && typeof json === "object"
              ? JSON.stringify(json)
              : "This call's input followed a compaction fence";
          return (
            <Tooltip title={tooltip}>
              <span>{String(locus)}</span>
            </Tooltip>
          );
        },
      },
      {
        field: "outputTokens",
        headerName: "Output",
        width: 90,
        type: "number",
        valueGetter: (_value, row) => row.outputTokens ?? 0,
        valueFormatter: (value) => formatTokens(Number(value)),
      },
      {
        field: "thinkingTokens",
        headerName: "Thinking",
        width: 100,
        type: "number",
        valueGetter: (_value, row) => row.thinkingTokens ?? 0,
        valueFormatter: (value) => formatTokens(Number(value)),
      },
      {
        field: "cacheReadTokens",
        headerName: "Cache read",
        width: 110,
        type: "number",
        valueGetter: (_value, row) => row.cacheReadTokens ?? 0,
        valueFormatter: (value) => formatTokens(Number(value)),
      },
      {
        field: "cacheWriteTokens",
        headerName: "Cache write",
        width: 110,
        type: "number",
        valueGetter: (_value, row) => row.cacheWriteTokens ?? 0,
        valueFormatter: (value) => formatTokens(Number(value)),
      },
      {
        field: "estimatedCostUsd",
        headerName: "Cost",
        width: 100,
        type: "number",
        valueGetter: (_value, row) => row.estimatedCostUsd,
        valueFormatter: (value) => (Number(value) > 0 ? `$${Number(value).toFixed(4)}` : "—"),
      },
    ],
    [latestId]
  );

  return (
    <DataGrid<TurnRow>
      rows={turns}
      getRowId={(row) => row.messageId}
      columns={columns}
      hideFooter
      disableRowSelectionOnClick
      noRowsMessage="No assistant turns yet."
      getRowClassName={(params) => (params.id === latestId ? "scout-latest-roundtrip" : "")}
      sx={{
        border: "none",
        height: "100%",
        "& .scout-latest-roundtrip": {
          backgroundColor: "action.hover",
        },
      }}
    />
  );
}

function formatTokens(tokens: number): string {
  if (!Number.isFinite(tokens) || tokens <= 0) {
    return "—";
  }
  if (tokens >= 1_000_000) {
    return `${(tokens / 1_000_000).toFixed(1)}M`;
  }
  if (tokens >= 1_000) {
    return `${Math.round(tokens / 100) / 10}K`;
  }
  return tokens.toString();
}

const DONUT_COLORS = [
  "#1f77b4",
  "#ff7f0e",
  "#2ca02c",
  "#d62728",
  "#9467bd",
  "#8c564b",
  "#e377c2",
  "#7f7f7f",
];
